import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  RefreshCw, ShieldCheck, Wallet, Server, KeyRound, Settings2, Rocket,
  AlertTriangle, CheckCircle2, Copy, ExternalLink, RadioTower
} from 'lucide-react';

const fmt = (n, d = 8) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: d });

const PROVIDER_META = {
  simulation: { label: 'Simulation (Dev)', color: 'text-[#848E9C]', desc: 'No real network — for dev only' },
  blockcypher: { label: 'BlockCypher', color: 'text-[#FCD535]', desc: 'BTC / LTC / DOGE — hosted API' },
  alchemy: { label: 'Alchemy (EVM)', color: 'text-[#3F8EFC]', desc: 'ETH / BSC / Polygon — JSON-RPC' },
  evm_rpc: { label: 'EVM JSON-RPC', color: 'text-[#3F8EFC]', desc: 'Any EVM RPC endpoint (Infura, Alchemy, your node)' },
  custom_rpc: { label: 'Custom RPC (your node)', color: 'text-[#0ECB81]', desc: 'Point at your own full node' },
  trongrid: { label: 'TronGrid', color: 'text-[#F6465D]', desc: 'TRX / USDT-TRC20 — hosted API' },
};

export default function HDWalletAdminPanel() {
  const [tab, setTab] = useState('overview');
  const [mode, setMode] = useState(null);
  const [chains, setChains] = useState([]);
  const [overview, setOverview] = useState(null);
  const [providerEditor, setProviderEditor] = useState(null); // {chain, provider_type, provider_config}
  const [revealer, setRevealer] = useState(null); // {symbol, password, mnemonic}
  const [auditLog, setAuditLog] = useState([]);
  const [monitor, setMonitor] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadMode = () => api.get('/admin/hd/mode').then(r => setMode(r.data)).catch(() => {});
  const loadChains = () => api.get('/admin/hd/chains').then(r => setChains(r.data.chains || [])).catch(() => {});
  const loadOverview = () => api.get('/admin/hd/wallets/overview').then(r => setOverview(r.data)).catch(() => {});
  const loadAudit = () => api.get('/admin/hd/audit?limit=100').then(r => setAuditLog(r.data.logs || [])).catch(() => {});
  const loadMonitor = () => api.get('/admin/hd/monitor/health').then(r => setMonitor(r.data)).catch(() => {});

  useEffect(() => { loadMode(); loadChains(); loadOverview(); }, []);
  useEffect(() => {
    if (tab === 'audit') loadAudit();
    if (tab === 'monitor') loadMonitor();
  }, [tab]); // eslint-disable-line

  const openProviderEditor = (chain) => {
    setProviderEditor({
      chain,
      provider_type: chain.provider_type || 'simulation',
      provider_config: {
        api_key: '', api_token: '', rpc_url: '', network: chain.network || 'mainnet', webhook_secret: '',
      },
    });
  };

  const saveProvider = async () => {
    if (!providerEditor) return;
    setBusy(true);
    try {
      // Only send non-empty fields (so admin can leave existing secrets intact)
      const cfg = {};
      for (const [k, v] of Object.entries(providerEditor.provider_config || {})) {
        if (v && v !== '') cfg[k] = v;
      }
      await api.put(`/admin/hd/chains/${providerEditor.chain.chain_id}/provider`, {
        provider_type: providerEditor.provider_type,
        provider_config: cfg,
      });
      toast.success(`Provider configured for ${providerEditor.chain.symbol}`);
      setProviderEditor(null);
      loadChains(); loadOverview();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  const initWallet = async (symbol) => {
    setBusy(true);
    try {
      await api.post(`/admin/hd/wallets/${symbol}/init`);
      toast.success(`HD wallet initialized for ${symbol}`);
      loadChains(); loadOverview();
    } catch (e) { toast.error(e.response?.data?.detail || 'Init failed'); }
    finally { setBusy(false); }
  };

  const exportBundle = async (symbol) => {
    try {
      const r = await api.get(`/admin/hd/wallets/${symbol}/export-encrypted`);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `bitgonode-${symbol}-encrypted-${Date.now()}.json`;
      a.click(); URL.revokeObjectURL(url);
      toast.success(`Encrypted bundle exported for ${symbol}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Export failed'); }
  };

  const openRevealer = (symbol) => setRevealer({ symbol, password: '', mnemonic: null, busy: false });
  const submitReveal = async () => {
    if (!revealer?.password) { toast.error('Enter your admin password'); return; }
    setRevealer(r => ({ ...r, busy: true }));
    try {
      const res = await api.post(`/admin/hd/wallets/${revealer.symbol}/reveal-mnemonic`,
        { password: revealer.password });
      setRevealer(r => ({ ...r, busy: false, mnemonic: res.data.mnemonic, xpub: res.data.xpub }));
    } catch (e) {
      setRevealer(r => ({ ...r, busy: false }));
      toast.error(e.response?.data?.detail || 'Reveal failed');
    }
  };

  const [sweeper, setSweeper] = useState(null); // {symbol, minBalance, dryRun, busy, result, toAddress}
  const openSweeper = (symbol) => setSweeper({ symbol, minBalance: '', dryRun: true, busy: false, result: null, toAddress: '' });
  const runSweep = async () => {
    if (!sweeper) return;
    setSweeper(s => ({ ...s, busy: true, result: null }));
    try {
      const body = { dry_run: sweeper.dryRun };
      if (sweeper.minBalance) body.min_balance = parseFloat(sweeper.minBalance);
      if (sweeper.toAddress) body.to_address = sweeper.toAddress.trim();
      const res = await api.post(`/admin/hd/wallets/${sweeper.symbol}/sweep`, body);
      setSweeper(s => ({ ...s, busy: false, result: res.data }));
      if (sweeper.dryRun) {
        toast.success(`Preview: ${res.data.addresses_swept} address(es), ~${res.data.swept_total} ${sweeper.symbol}`);
      } else {
        toast.success(`Swept ${res.data.swept_total} ${sweeper.symbol} from ${res.data.addresses_swept} addresses`);
      }
    } catch (e) {
      setSweeper(s => ({ ...s, busy: false }));
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Sweep failed'));
    }
  };

  return (
    <div className="space-y-4" data-testid="hd-wallet-admin-panel">
      {/* Mode banner */}
      {mode && (
        <div className={`border p-3 flex items-center justify-between flex-wrap gap-2 ${
          mode.wallet_mode === 'live'
            ? 'border-[#0ECB81]/40 bg-[#0ECB81]/5'
            : 'border-[#FCD535]/40 bg-[#FCD535]/5'
        }`} data-testid="hd-mode-banner">
          <div className="flex items-center gap-3">
            {mode.wallet_mode === 'live' ? (
              <RadioTower className="w-5 h-5 text-[#0ECB81]" />
            ) : (
              <Server className="w-5 h-5 text-[#FCD535]" />
            )}
            <div>
              <div className="text-white text-sm font-semibold flex items-center gap-2">
                Wallet mode: <span className={mode.wallet_mode === 'live' ? 'text-[#0ECB81]' : 'text-[#FCD535]'}>{mode.wallet_mode.toUpperCase()}</span>
                {mode.encryption_configured ? (
                  <span className="inline-flex items-center gap-1 text-[10px] text-[#0ECB81]"><ShieldCheck className="w-3 h-3" /> encryption OK</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] text-[#F6465D]"><AlertTriangle className="w-3 h-3" /> WALLET_ENCRYPTION_KEY missing!</span>
                )}
              </div>
              <div className="text-[10px] text-[#848E9C] mt-0.5">
                {mode.wallet_mode === 'live'
                  ? 'Live mode — real blockchain provider adapters are active. Handle with care.'
                  : 'Simulation mode — no real network calls. Set WALLET_MODE=live in backend/.env and redeploy to go live.'}
              </div>
            </div>
          </div>
          <div className="text-[10px] text-[#848E9C] font-mono">
            {mode.available_providers?.length} adapters loaded
          </div>
        </div>
      )}

      {/* Sub-tabs */}
      <div className="inline-flex border border-[#2B3139] p-0.5">
        {[['overview', 'Overview'], ['chains', 'Chains & Providers'], ['monitor', 'Monitor'], ['audit', 'Audit Log']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-3 py-1 text-[10px] uppercase font-mono tracking-wider ${tab === k ? 'bg-[#FCD535] text-[#0B0E11]' : 'text-[#B7BDC6] hover:text-white'}`}
            data-testid={`hd-tab-${k}`}
          >{l}</button>
        ))}
      </div>

      {tab === 'overview' && overview && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[
              ['Chains total', overview.totals.chains],
              ['Chains enabled', overview.totals.chains_enabled],
              ['Wallets initialized', overview.totals.wallets_initialized],
              ['Addresses derived', overview.totals.addresses_total],
              ['Deposits confirmed', overview.totals.deposits_confirmed_total],
            ].map(([label, v]) => (
              <div key={label} className="border border-[#2B3139] bg-[#0B0E11] p-3">
                <div className="text-[10px] uppercase tracking-wider text-[#848E9C]">{label}</div>
                <div className="text-white font-mono text-xl mt-1">{v}</div>
              </div>
            ))}
          </div>
          <div className="border border-[#2B3139] bg-[#181A20]">
            <div className="hidden md:grid grid-cols-12 px-4 py-2 text-[10px] uppercase tracking-wider text-[#848E9C] border-b border-[#2B3139]">
              <div className="col-span-2">Chain</div>
              <div className="col-span-2">Provider</div>
              <div className="col-span-1">Wallet</div>
              <div className="col-span-1">Addresses</div>
              <div className="col-span-1">Next idx</div>
              <div className="col-span-1">Pending</div>
              <div className="col-span-1">Confirmed</div>
              <div className="col-span-3">xPub</div>
            </div>
            {overview.chains.map(c => (
              <div key={c.chain_id} className="grid grid-cols-2 md:grid-cols-12 gap-2 px-4 py-3 border-b border-[#2B3139]/40 items-center text-xs" data-testid={`overview-row-${c.symbol}`}>
                <div className="md:col-span-2 text-white font-semibold">{c.symbol} <span className="text-[10px] text-[#848E9C]">· {c.network}</span></div>
                <div className="md:col-span-2 text-[#B7BDC6]">
                  {(() => {
                    const chain = chains.find(x => x.chain_id === c.chain_id);
                    const pt = chain?.provider_type || 'simulation';
                    return <span className={PROVIDER_META[pt]?.color || 'text-[#848E9C]'}>{PROVIDER_META[pt]?.label || pt}</span>;
                  })()}
                </div>
                <div className="md:col-span-1">
                  {c.wallet_initialized ? (
                    <span className="inline-flex items-center gap-1 text-[10px] text-[#0ECB81]"><CheckCircle2 className="w-3 h-3" /> Init</span>
                  ) : (
                    <Button size="sm" onClick={() => initWallet(c.symbol)} className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] rounded-sm h-6 text-[10px] px-2" data-testid={`init-wallet-${c.symbol}`}>
                      Initialize
                    </Button>
                  )}
                </div>
                <div className="md:col-span-1 font-mono text-white">{c.address_count}</div>
                <div className="md:col-span-1 font-mono text-[#B7BDC6]">{c.next_index}</div>
                <div className="md:col-span-1 font-mono text-[#FCD535]">{c.pending_deposits}</div>
                <div className="md:col-span-1 font-mono text-[#0ECB81]">{c.confirmed_deposits}</div>
                <div className="md:col-span-3 font-mono text-[9px] text-[#848E9C] truncate flex items-center gap-1">
                  {c.xpub ? (
                    <>
                      <span className="truncate">{c.xpub.slice(0, 32)}…</span>
                      <button onClick={() => { navigator.clipboard.writeText(c.xpub); toast.success('xPub copied'); }} className="text-[#848E9C] hover:text-[#FCD535]" title="Copy xPub">
                        <Copy className="w-3 h-3" />
                      </button>
                    </>
                  ) : <span className="text-[#848E9C]">—</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'chains' && (
        <div className="border border-[#2B3139] bg-[#181A20]">
          <div className="hidden md:grid grid-cols-12 px-4 py-2 text-[10px] uppercase tracking-wider text-[#848E9C] border-b border-[#2B3139]">
            <div className="col-span-2">Symbol / Name</div>
            <div className="col-span-2">Provider</div>
            <div className="col-span-1">Network</div>
            <div className="col-span-1">Confirms</div>
            <div className="col-span-2">Min dep / wd</div>
            <div className="col-span-2">Hot wallet</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {chains.map(c => {
            const meta = PROVIDER_META[c.provider_type || 'simulation'];
            return (
              <div key={c.chain_id} className="grid grid-cols-2 md:grid-cols-12 gap-2 px-4 py-3 border-b border-[#2B3139]/40 items-center text-xs" data-testid={`chain-row-${c.symbol}`}>
                <div className="md:col-span-2 text-white">
                  <div className="font-semibold">{c.symbol}</div>
                  <div className="text-[10px] text-[#848E9C]">{c.name}</div>
                </div>
                <div className="md:col-span-2">
                  <div className={`text-xs ${meta?.color || 'text-[#848E9C]'}`}>{meta?.label || c.provider_type}</div>
                  <div className="text-[9px] text-[#848E9C]">{meta?.desc}</div>
                </div>
                <div className="md:col-span-1 text-[#B7BDC6] font-mono">{c.network}</div>
                <div className="md:col-span-1 font-mono text-white">{c.required_confirmations}</div>
                <div className="md:col-span-2 font-mono text-[#B7BDC6] text-[10px]">
                  <div>dep: {fmt(c.min_deposit, 6)}</div>
                  <div>wd: {fmt(c.min_withdrawal, 6)}</div>
                </div>
                <div className="md:col-span-2 font-mono text-[9px] text-[#848E9C] truncate">
                  {c.hot_wallet_address || <span className="italic">unset</span>}
                </div>
                <div className="md:col-span-2 text-right flex items-center justify-end gap-1">
                  <Button size="sm" onClick={() => openProviderEditor(c)}
                    className="bg-[#2B3139] hover:bg-[#3A424C] text-[#FCD535] rounded-sm h-7 text-[10px] px-2"
                    data-testid={`chain-provider-${c.symbol}`}
                  ><KeyRound className="w-3 h-3 mr-1" />Provider</Button>
                  {c.wallet_initialized && (
                    <>
                      <Button size="sm" onClick={() => exportBundle(c.symbol)}
                        className="bg-[#2B3139] hover:bg-[#3A424C] text-white rounded-sm h-7 text-[10px] px-2"
                        title="Export encrypted seed backup (needs WALLET_ENCRYPTION_KEY to decrypt)"
                        data-testid={`chain-export-${c.symbol}`}
                      >Backup</Button>
                      <Button size="sm" onClick={() => openSweeper(c.symbol)}
                        className="bg-[#0ECB81]/10 hover:bg-[#0ECB81]/20 border border-[#0ECB81]/40 text-[#0ECB81] rounded-sm h-7 text-[10px] px-2"
                        title="Sweep all user deposits to your hot wallet"
                        data-testid={`chain-sweep-${c.symbol}`}
                      >Sweep</Button>
                      <Button size="sm" onClick={() => openRevealer(c.symbol)}
                        className="bg-[#F6465D]/10 hover:bg-[#F6465D]/20 border border-[#F6465D]/40 text-[#F6465D] rounded-sm h-7 text-[10px] px-2"
                        title="Reveal the 24-word seed phrase (one-time, requires password)"
                        data-testid={`chain-reveal-${c.symbol}`}
                      >Reveal Seed</Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === 'monitor' && (
        <div className="space-y-3">
          <Button onClick={loadMonitor} className="bg-[#2B3139] hover:bg-[#3A424C] text-white rounded-sm h-8 text-xs" data-testid="hd-monitor-refresh">
            <RefreshCw className="w-3 h-3 mr-1.5" /> Refresh
          </Button>
          {monitor && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="border border-[#2B3139] bg-[#0B0E11] p-3">
                  <div className="text-[10px] uppercase tracking-wider text-[#848E9C]">Pending deposits</div>
                  <div className="text-[#FCD535] font-mono text-xl mt-1">{monitor.pending_deposits}</div>
                </div>
                <div className="border border-[#2B3139] bg-[#0B0E11] p-3">
                  <div className="text-[10px] uppercase tracking-wider text-[#848E9C]">Failed scan events (all-time)</div>
                  <div className="text-[#F6465D] font-mono text-xl mt-1">{monitor.failed_events}</div>
                </div>
              </div>
              <div className="border border-[#2B3139] bg-[#181A20]">
                <div className="px-4 py-2 text-[10px] uppercase tracking-wider text-[#848E9C] border-b border-[#2B3139]">Recent events (50)</div>
                {monitor.recent_events.length === 0 ? (
                  <div className="p-6 text-center text-[#848E9C] text-sm">No events yet.</div>
                ) : monitor.recent_events.map(e => (
                  <div key={e.event_id} className="px-4 py-2 border-b border-[#2B3139]/40 text-xs flex items-center gap-3">
                    <span className={`w-14 uppercase text-[9px] font-mono ${
                      e.level === 'error' ? 'text-[#F6465D]' :
                      e.level === 'warn' ? 'text-[#F0B90B]' : 'text-[#0ECB81]'
                    }`}>{e.level}</span>
                    <span className="text-[#B7BDC6] truncate flex-1">{e.message}</span>
                    <span className="text-[9px] text-[#848E9C] font-mono">{new Date(e.created_at).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {tab === 'audit' && (
        <div className="space-y-3">
          <Button onClick={loadAudit} className="bg-[#2B3139] hover:bg-[#3A424C] text-white rounded-sm h-8 text-xs" data-testid="hd-audit-refresh">
            <RefreshCw className="w-3 h-3 mr-1.5" /> Refresh
          </Button>
          <div className="border border-[#2B3139] bg-[#181A20]">
            <div className="hidden md:grid grid-cols-12 px-4 py-2 text-[10px] uppercase tracking-wider text-[#848E9C] border-b border-[#2B3139]">
              <div className="col-span-2">When</div>
              <div className="col-span-2">Admin</div>
              <div className="col-span-2">IP</div>
              <div className="col-span-2">Action</div>
              <div className="col-span-4">Meta</div>
            </div>
            {auditLog.length === 0 ? (
              <div className="p-6 text-center text-[#848E9C] text-sm">No audit entries yet.</div>
            ) : auditLog.map(l => (
              <div key={l.log_id} className="grid grid-cols-2 md:grid-cols-12 gap-2 px-4 py-2 border-b border-[#2B3139]/40 items-start text-xs" data-testid={`audit-row-${l.log_id}`}>
                <div className="md:col-span-2 text-[#B7BDC6] font-mono text-[10px]">{new Date(l.created_at).toLocaleString()}</div>
                <div className="md:col-span-2 text-white truncate">{l.admin_email || l.admin_id}</div>
                <div className="md:col-span-2 text-[#848E9C] font-mono text-[10px]">{l.ip || '—'}</div>
                <div className="md:col-span-2 text-[#FCD535] font-mono">{l.action}</div>
                <div className="md:col-span-4 text-[#B7BDC6] font-mono text-[9px] break-all">{JSON.stringify(l.meta || {})}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Provider editor dialog */}
      <Dialog open={!!providerEditor} onOpenChange={(o) => !o && setProviderEditor(null)}>
        <DialogContent className="bg-[#181A20] border-[#2B3139] text-white rounded-sm max-w-lg max-h-[85vh] overflow-y-auto">
          {providerEditor && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <KeyRound className="w-5 h-5 text-[#FCD535]" />
                  Provider · {providerEditor.chain.symbol}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="text-xs text-[#848E9C] leading-relaxed">
                  Choose which blockchain integration this chain uses. In simulation mode all providers behave like the simulator regardless of selection — set WALLET_MODE=live in backend/.env to activate them.
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#848E9C]">Provider type</label>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {(mode?.available_providers || []).map(pt => {
                      const m = PROVIDER_META[pt] || {};
                      const selected = providerEditor.provider_type === pt;
                      return (
                        <button key={pt} type="button"
                          onClick={() => setProviderEditor({ ...providerEditor, provider_type: pt })}
                          className={`text-left px-3 py-2 border ${selected ? 'bg-[#FCD535]/10 border-[#FCD535]' : 'bg-[#0B0E11] border-[#2B3139] hover:border-[#3A424C]'}`}
                          data-testid={`provider-select-${pt}`}
                        >
                          <div className={`text-xs uppercase font-mono tracking-wider ${m.color || 'text-[#B7BDC6]'}`}>{m.label || pt}</div>
                          <div className="text-[10px] text-[#848E9C] mt-1">{m.desc || ''}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Dynamic config fields per provider */}
                {providerEditor.provider_type !== 'simulation' && (
                  <div className="space-y-3 border-t border-[#2B3139] pt-4">
                    <div className="text-[10px] uppercase tracking-wider text-[#FCD535]">Provider config</div>
                    {['blockcypher'].includes(providerEditor.provider_type) && (
                      <>
                        <ConfigInput label="API token (BlockCypher)" placeholder="Leave blank to keep existing"
                          k="api_token" editor={providerEditor} setEditor={setProviderEditor} testid="cfg-api-token" />
                        <ConfigInput label="Network" placeholder="main / test3"
                          k="network" editor={providerEditor} setEditor={setProviderEditor} testid="cfg-network" />
                        <ConfigInput label="Webhook HMAC secret" placeholder="Random long string"
                          k="webhook_secret" editor={providerEditor} setEditor={setProviderEditor} testid="cfg-webhook-secret" />
                      </>
                    )}
                    {['alchemy'].includes(providerEditor.provider_type) && (
                      <>
                        <ConfigInput label="Alchemy API key" placeholder="Leave blank to keep existing"
                          k="api_key" editor={providerEditor} setEditor={setProviderEditor} testid="cfg-api-key" />
                        <ConfigInput label="Network" placeholder="mainnet / sepolia / bnb-mainnet"
                          k="network" editor={providerEditor} setEditor={setProviderEditor} testid="cfg-network" />
                        <ConfigInput label="Webhook signing key" placeholder="Leave blank to keep existing"
                          k="webhook_secret" editor={providerEditor} setEditor={setProviderEditor} testid="cfg-webhook-secret" />
                      </>
                    )}
                    {['evm_rpc', 'custom_rpc'].includes(providerEditor.provider_type) && (
                      <>
                        <ConfigInput label="RPC URL (your node or hosted)" placeholder="https://your-node:8545"
                          k="rpc_url" editor={providerEditor} setEditor={setProviderEditor} testid="cfg-rpc-url" />
                        <ConfigInput label="Network" placeholder="mainnet / sepolia / …"
                          k="network" editor={providerEditor} setEditor={setProviderEditor} testid="cfg-network" />
                      </>
                    )}
                    {['trongrid'].includes(providerEditor.provider_type) && (
                      <>
                        <ConfigInput label="TronGrid API key" placeholder="Leave blank to keep existing"
                          k="api_key" editor={providerEditor} setEditor={setProviderEditor} testid="cfg-api-key" />
                        <ConfigInput label="Network" placeholder="mainnet / shasta / nile"
                          k="network" editor={providerEditor} setEditor={setProviderEditor} testid="cfg-network" />
                      </>
                    )}
                    <div className="text-[9px] text-[#848E9C] leading-relaxed border-l-2 border-[#2B3139] pl-2">
                      Secrets shown as ***CONFIGURED*** in responses. Leave field blank to keep the existing value; type a new value to overwrite.
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button onClick={() => setProviderEditor(null)} className="bg-transparent border border-[#2B3139] hover:bg-[#2B3139] text-white rounded-sm">Cancel</Button>
                <Button onClick={saveProvider} disabled={busy}
                  className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] rounded-sm font-semibold"
                  data-testid="provider-save"
                >{busy ? 'Saving…' : 'Save'}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reveal Seed Phrase modal */}
      <Dialog open={!!revealer} onOpenChange={(o) => !o && setRevealer(null)}>
        <DialogContent className="bg-[#181A20] border-[#2B3139] text-white rounded-sm max-w-lg" data-testid="reveal-seed-dialog">
          {revealer && !revealer.mnemonic && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-[#F6465D]">
                  <AlertTriangle className="w-4 h-4" /> Reveal seed phrase — {revealer.symbol}
                </DialogTitle>
              </DialogHeader>
              <div className="text-xs text-[#B7BDC6] space-y-2">
                <div className="border border-[#F6465D]/40 bg-[#F6465D]/10 p-3 rounded-sm space-y-1">
                  <div className="text-[#F6465D] font-semibold">⚠️ EXTREME CAUTION</div>
                  <div>• Anyone with these 24 words controls the entire wallet.</div>
                  <div>• Write them on paper — do NOT screenshot, photograph, or save digitally.</div>
                  <div>• Store the paper in 2+ physically secure locations (safe, safety deposit box).</div>
                  <div>• This action is written to the audit log with your IP + timestamp.</div>
                </div>
                <div className="pt-1">
                  <label className="text-[10px] uppercase tracking-wider text-[#848E9C]">Confirm your admin password</label>
                  <Input type="password" value={revealer.password}
                    onChange={e => setRevealer(r => ({ ...r, password: e.target.value }))}
                    placeholder="Your BITGONODE admin password"
                    className="mt-1 bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono focus-visible:border-[#FCD535]"
                    data-testid="reveal-seed-password" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setRevealer(null)} className="border-[#2B3139] bg-[#181A20] text-white hover:bg-[#2B3139] rounded-sm" data-testid="reveal-seed-cancel">Cancel</Button>
                <Button onClick={submitReveal} disabled={revealer.busy || !revealer.password}
                  className="bg-[#F6465D] hover:bg-[#E33A50] text-white rounded-sm" data-testid="reveal-seed-submit">
                  {revealer.busy ? 'Revealing…' : 'Reveal seed phrase'}
                </Button>
              </DialogFooter>
            </>
          )}
          {revealer && revealer.mnemonic && (
            <>
              <DialogHeader>
                <DialogTitle className="text-[#0ECB81]">Your {revealer.symbol} seed phrase</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="border border-[#0ECB81]/40 bg-[#0ECB81]/5 p-3 rounded-sm">
                  <div className="text-[10px] uppercase tracking-wider text-[#848E9C] mb-2">Write these words down, in order, on paper</div>
                  <div className="grid grid-cols-3 gap-2" data-testid="reveal-seed-words">
                    {revealer.mnemonic.split(' ').map((w, i) => (
                      <div key={i} className="border border-[#2B3139] bg-[#0B0E11] rounded-sm px-2 py-1.5 flex items-center gap-2">
                        <span className="text-[9px] text-[#5E6673] font-mono w-5 text-right">{i + 1}</span>
                        <span className="text-white font-mono text-xs">{w}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="border border-[#F6465D]/40 bg-[#F6465D]/10 p-2 rounded-sm text-[11px] text-[#F6465D]">
                  ⚠️ Close this window as soon as you've written the words down. It will NOT be shown again automatically — you'd have to re-enter your password.
                </div>
                <div className="text-[10px] text-[#848E9C] font-mono truncate">xpub: {revealer.xpub}</div>
              </div>
              <DialogFooter>
                <Button onClick={() => setRevealer(null)}
                  className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] rounded-sm w-full"
                  data-testid="reveal-seed-close">
                  I've written it down — close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Sweep to Hot Wallet modal */}
      <Dialog open={!!sweeper} onOpenChange={(o) => !o && setSweeper(null)}>
        <DialogContent className="bg-[#181A20] border-[#2B3139] text-white rounded-sm max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="sweep-dialog">
          {sweeper && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-[#0ECB81]">
                  <RadioTower className="w-4 h-4" /> Sweep {sweeper.symbol} deposits → Hot Wallet
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-xs">
                <div className="border border-[#2B3139] bg-[#0B0E11] p-3 rounded-sm text-[#B7BDC6] space-y-1">
                  <div>• Scans every derived user deposit address on-chain</div>
                  <div>• Sends anything above the min-balance threshold to your hot wallet</div>
                  <div>• Each sweep is a real gas-paying transaction (reserves ~0.0005 ETH per address for gas)</div>
                  <div className="text-[#F0B90B]">• Preview with <strong>Dry-run</strong> first to see the plan without broadcasting</div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-[#848E9C]">Min balance (skip dust)</label>
                    <Input value={sweeper.minBalance} onChange={e => setSweeper(s => ({ ...s, minBalance: e.target.value }))}
                      placeholder="e.g. 0.005"
                      className="mt-1 bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono focus-visible:border-[#FCD535]"
                      data-testid="sweep-min-balance" />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-[#B7BDC6] cursor-pointer">
                      <input type="checkbox" checked={sweeper.dryRun}
                        onChange={e => setSweeper(s => ({ ...s, dryRun: e.target.checked }))}
                        data-testid="sweep-dry-run" />
                      Dry-run (preview only, don't broadcast)
                    </label>
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-[#848E9C]">
                    Destination address <span className="normal-case text-[#5E6673]">— leave blank to use the chain's saved hot wallet</span>
                  </label>
                  <Input value={sweeper.toAddress} onChange={e => setSweeper(s => ({ ...s, toAddress: e.target.value }))}
                    placeholder="0x2C03D441C86cc9C8cafb4851124954FE677dbf19 (your hot wallet)"
                    className="mt-1 bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono focus-visible:border-[#FCD535]"
                    data-testid="sweep-to-address" />
                </div>
                {sweeper.result && (
                  <div className="border border-[#0ECB81]/40 bg-[#0ECB81]/5 rounded-sm p-3 space-y-2" data-testid="sweep-result">
                    <div className="grid grid-cols-4 gap-2 text-[11px]">
                      <div><div className="text-[#848E9C] uppercase text-[9px]">Hot wallet</div><div className="font-mono text-white truncate">{sweeper.result.hot_wallet}</div></div>
                      <div><div className="text-[#848E9C] uppercase text-[9px]">Scanned</div><div className="font-mono text-white">{sweeper.result.addresses_scanned}</div></div>
                      <div><div className="text-[#848E9C] uppercase text-[9px]">Swept</div><div className="font-mono text-[#0ECB81]">{sweeper.result.addresses_swept}</div></div>
                      <div><div className="text-[#848E9C] uppercase text-[9px]">Total</div><div className="font-mono text-[#FCD535]">{sweeper.result.swept_total}</div></div>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {(sweeper.result.results || []).map((r, i) => (
                        <div key={i} className="grid grid-cols-12 gap-1 py-1 border-b border-[#2B3139]/40 text-[10px] items-center">
                          <div className="col-span-4 font-mono text-[#EAECEF] truncate" title={r.address}>{r.address?.slice(0,10)}…{r.address?.slice(-6)}</div>
                          <div className="col-span-2 font-mono text-[#B7BDC6] text-right">{r.balance}</div>
                          <div className="col-span-2 font-mono text-[#FCD535] text-right">{r.sweep_amount}</div>
                          <div className="col-span-2 text-center">
                            <span className={`px-1.5 py-0.5 rounded-sm text-[9px] uppercase ${
                              r.status === 'broadcast' ? 'bg-[#0ECB81]/20 text-[#0ECB81]' :
                              r.status === 'would_sweep' ? 'bg-[#F0B90B]/20 text-[#F0B90B]' :
                              'bg-[#F6465D]/20 text-[#F6465D]'
                            }`}>{r.status}</span>
                          </div>
                          <div className="col-span-2 font-mono text-[9px] text-[#5E6673] truncate" title={r.tx_hash || r.error}>{r.tx_hash?.slice(0,10) || r.error || ''}</div>
                        </div>
                      ))}
                      {(sweeper.result.results || []).length === 0 && (
                        <div className="text-center text-[#848E9C] py-4">No addresses met the min-balance threshold.</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSweeper(null)} className="border-[#2B3139] bg-[#181A20] text-white hover:bg-[#2B3139] rounded-sm" data-testid="sweep-close">Close</Button>
                <Button onClick={runSweep} disabled={sweeper.busy}
                  className={sweeper.dryRun
                    ? "bg-[#F0B90B] hover:bg-[#FCD535] text-[#0B0E11] rounded-sm font-semibold"
                    : "bg-[#0ECB81] hover:bg-[#0BB574] text-white rounded-sm font-semibold"}
                  data-testid="sweep-run">
                  {sweeper.busy ? 'Running…' : (sweeper.dryRun ? 'Preview sweep' : 'Sweep now (broadcast!)')}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ConfigInput({ label, placeholder, k, editor, setEditor, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-[#848E9C]">{label}</label>
      <Input value={editor.provider_config?.[k] || ''}
        onChange={e => setEditor({ ...editor, provider_config: { ...editor.provider_config, [k]: e.target.value } })}
        placeholder={placeholder}
        className="mt-1 bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono focus-visible:border-[#FCD535]"
        data-testid={testid} />
    </div>
  );
}
